import type {
  CellData,
  ColumnDef,
  ColumnMeta,
  RowData,
  TableFeatures,
} from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { cacheByItem } from '../../helpers/cache-by-item';
import type { AppRow, AppTableFeatures } from './features';

/**
 * Extend TanStack Table's ColumnMeta to include application-specific fields.
 * This allows us to attach custom metadata to columns for CSV export, styling, etc.
 */
declare module '@tanstack/react-table' {
  interface ColumnMeta<
    in out TFeatures extends TableFeatures,
    in out TData extends RowData,
    TValue extends CellData = CellData,
  > {
    /** Minimum column width in pixels */
    minWidth: number;
    /** Maximum column width in pixels (columns with maxWidth can expand) */
    maxWidth?: number;

    /** Convert value to string representation (for CSV fallback, print, etc.) */
    toString: (value: TData) => string;
    /** Custom CSV export formatter - returns key-value pairs for multiple CSV columns */
    toCSV?: (item: TData) => { [key: string]: string };

    /** Plain-language column description, surfaced via an info icon in the header meta-row. */
    gloss?: ReactNode;

    /**
     * This row's place in the column's ordering, as a value that can be
     * compared directly. Present only when the ordering is a pure function of
     * one derived value per row — which is every column except the ones passing
     * their own `sortingFn`.
     *
     * createSortedRowModel reads this to compute each row's key once per sort
     * rather than inside the comparator. A column without it still sorts, via
     * the `sortFn` comparator; it just doesn't get decorated.
     */
    sortKey?: (value: TData) => string;
  }
}

/** Minimal row type for cell rendering — keeps wrapProductColumn constructable */
export type CellRow<TData extends RowData> = Pick<AppRow<TData>, 'original' | 'id'>;

/** Minimal table surface needed by cell renderers (visual index lookup) */
export type CellTable = {
  getSortedRowModel: () => { flatRows: ReadonlyArray<{ id: string }> };
};

/** Minimal row type for sorting (subset of TanStack Row) */
type SortRow<TData extends RowData> = Pick<AppRow<TData>, 'original'>;

export type StrictColumnDef<TData extends RowData> = Omit<
  ColumnDef<AppTableFeatures, TData, unknown>,
  'accessorFn' | 'cell' | 'sortFn' | 'meta' | 'header'
> & {
  id: string;
  header: string | (() => ReactNode);
  accessorFn: (value: TData) => string;
  /**
   * Custom cell renderer. Compatible with TanStack CellContext — getValue is
   * typed unknown because v9 erases TValue to unknown in the columns option;
   * defineColumn derives the string value from accessorFn instead.
   */
  cell?: (props: { table: CellTable; row: CellRow<TData>; getValue: () => unknown }) => ReactNode;
  sortFn?: (rowA: SortRow<TData>, rowB: SortRow<TData>, columnId: string) => number;
  meta: ColumnMeta<AppTableFeatures, TData, unknown>;
};

/**
 * Configuration for defining a TanStack column with application-specific features.
 * Provides a clean API similar to the legacy ColumnDefinition while returning
 * a native TanStack ColumnDef.
 */
export type ColumnConfig<T extends RowData> = {
  /** Column identifier and header label */
  id: string;
  /** Minimum column width in pixels */
  minWidth: number;
  /** Maximum column width in pixels (columns with maxWidth can expand) */
  maxWidth?: number;

  /** Convert value to string representation (for CSV fallback, print, etc.) */
  accessorFn: (value: T) => string;

  /** Custom comparison function for sorting */
  sortingFn?: (rowA: T, rowB: T) => number;

  /** Custom cell renderer. Destructure only what you need. */
  cell?: (props: { table: CellTable; row: CellRow<T>; item: T; value: string }) => ReactNode;

  /** Custom CSV export formatter - returns key-value pairs for multiple CSV columns */
  toCSV?: (row: T) => { [key: string]: string };

  /**
   * Plain-language column description. Rendered as an info icon in the header
   * cell's meta-row (see table.tsx) — never inside the label's box, so it can't
   * collide with a wrapping multi-line label.
   */
  gloss?: ReactNode;
};

/**
 * Create a TanStack ColumnDef with application-specific configuration.
 *
 * @example
 * ```tsx
 * const itemCodeColumn = defineColumn<Product>({
 *   id: 'Item Code',
 *   minWidth: 55,
 *   getValue: (product) => product.item_code || product.suggested_item_code,
 *   cell: ({ value }) => <span>{value}</span>,
 * });
 * ```
 */
export function defineColumn<T extends RowData>(config: ColumnConfig<T>): StrictColumnDef<T> {
  const {
    id,
    minWidth,
    maxWidth,
    accessorFn,
    cell,
    toCSV,
    gloss,
    sortingFn: customSortingFn,
  } = config;

  // The accessors here are derived values — reorderLabel, formatUnitsQuantity,
  // underThreshold — so deriving one key per row beats recomputing inside the
  // comparator. The cache belongs to this defineColumn call: a column whose
  // accessor reads settings (useReorder) gets a fresh one as soon as those
  // settings build a new column.
  const sortKey = cacheByItem<T, string>(accessorFn);

  // Default comparison function if not provided by the user
  const compare =
    customSortingFn ?? ((rowA: T, rowB: T) => sortKey(rowA).localeCompare(sortKey(rowB)));

  return {
    id,
    header: id,
    accessorFn,

    cell: cell
      ? ({ row, table }) => {
          return cell({ item: row.original, row, value: accessorFn(row.original), table });
        }
      : ({ row }) => accessorFn(row.original),

    sortFn: (rowA, rowB, _columnId) => compare(rowA.original, rowB.original),
    enableSorting: true,

    // rowSortingFeature defaults every column to `sortUndefined: 1`, which makes
    // the library's compareRows call row.getValue() on BOTH rows of every
    // comparison purely to check for undefined. accessorFn returns a string, so
    // that probe can never change an outcome — it was a full second of the
    // ~13.7s spent typing into a 13k-row table. Opt out.
    sortUndefined: false,

    // Size configuration
    minSize: minWidth,
    maxSize: maxWidth,
    size: minWidth,

    // Store properties in meta for CSV export, print, sizing, etc.
    meta: {
      toString: accessorFn,
      toCSV,
      gloss,
      minWidth,
      maxWidth,
      // A custom sortingFn is an opaque comparator — there's no single value
      // per row to precompute — so those columns publish no key and keep
      // sorting through sortFn.
      sortKey: customSortingFn ? undefined : sortKey,
    },
  };
}

/** Type guard to narrow ColumnDef to StrictColumnDef (for extracting columns from TanStack Table API) */
export function isStrictColumnDef<T extends RowData>(
  col: ColumnDef<AppTableFeatures, T, unknown>
): col is ColumnDef<AppTableFeatures, T, unknown> & StrictColumnDef<T> {
  return typeof col.id === 'string' && col.meta !== undefined;
}
